import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LineItemPicker from '../components/e2i/LineItemPicker';
import { CostTypeRow, CostTypesEditor, JobTypeRow, JobTypesEditor, RoleSelect, StageRow, StagesEditor } from '../components/e2i/SetupEditors';
import useCompanyId from '../components/e2i/useCompanyId';
import e2iApi from '../services/e2iApi';
import { showSnackbar } from '../store/slices/uiSlice';
import type { DocMode, E2IConfig, E2ISettings, E2ISetupData } from '../types/e2i';
import { errorText, formatPct, numberText, parseNumber } from '../utils/e2i';

/**
 * Enquiry to Invoice › Setup (per company): cost types and their default markups, profit, VAT, validity and terms;
 * job types; the quotation and job approval stages and the finance role; how the SAP documents are made.
 */

interface SetupForm {
  costTypes: CostTypeRow[];
  profit: string;
  vatCode: string;
  validity: string;
  terms: string;
  jobTypes: JobTypeRow[];
  quotationStages: StageRow[];
  calStages: StageRow[];
  financeRole: string;
  docMode: DocMode;
  defaultSalesItem: string | null;
  defaultRevenueAccount: string | null;
  branch: string;
  dim5SalesItem: boolean;
  postQuotations: boolean;
}

type Section = 'pricing' | 'jobs' | 'approvals' | 'sap';

const formOf = (s: E2ISettings): SetupForm => ({
  costTypes: (s.costTypes || []).map((c) => ({ code: c.code, name: c.name, markup: numberText(c.markup ?? 0) })),
  profit: numberText(s.profitPct ?? 0),
  vatCode: s.vatCode || '',
  validity: numberText(s.quoteValidityDays ?? 30),
  terms: s.terms || '',
  jobTypes: (s.jobTypes || []).map((j) => ({ code: j.code, name: j.name, salesItem: j.salesItem ?? null, account: j.account ?? null })),
  quotationStages: (s.quotationStages || []).map((st) => ({ name: st.name, role: st.role, minAmount: numberText(st.minAmount) })),
  calStages: (s.calStages || []).map((st) => ({ name: st.name, role: st.role, minAmount: '' })),
  financeRole: s.financeRole || '',
  docMode: s.docMode === 'Service' ? 'Service' : 'Item',
  defaultSalesItem: s.defaultSalesItem ?? null,
  defaultRevenueAccount: s.defaultRevenueAccount ?? null,
  branch: s.branchId !== null && s.branchId !== undefined ? String(s.branchId) : '',
  dim5SalesItem: s.dim5SalesItem !== false,
  postQuotations: s.postQuotations !== false,
});

/** The settings to save, or what stops the save. */
function settingsOf(f: SetupForm): { settings: E2ISettings | null; problems: string[] } {
  const problems: string[] = [];
  const codes = new Set<string>();
  const costTypes = f.costTypes.map((c) => {
    const code = c.code.trim();
    if (!code) problems.push('Every cost type needs a code');
    else if (codes.has(code)) problems.push(`Cost type ${code} is listed twice`);
    codes.add(code);
    if (!c.name.trim()) problems.push(`Cost type ${code || '(new)'} needs a name`);
    const markup = parseNumber(c.markup);
    if (markup === null || markup < 0 || markup > 1000) problems.push(`The markup of ${code || 'a cost type'} must be 0 to 1000%`);
    return { code, name: c.name.trim(), markup: markup ?? 0 };
  });
  if (!costTypes.length) problems.push('Keep at least one cost type');
  const profit = parseNumber(f.profit);
  if (profit === null || profit < 0 || profit > 1000) problems.push('The profit must be 0 to 1000%');
  const validity = Number(f.validity);
  if (!Number.isInteger(validity) || validity < 1 || validity > 3650) problems.push('The validity is 1 to 3650 days');
  const jobCodes = new Set<string>();
  const jobTypes = f.jobTypes.map((j) => {
    const code = j.code.trim();
    if (!code) problems.push('Every job type needs a code');
    else if (jobCodes.has(code)) problems.push(`Job type ${code} is listed twice`);
    jobCodes.add(code);
    if (!j.name.trim()) problems.push(`Job type ${code || '(new)'} needs a name`);
    return { code, name: j.name.trim(), salesItem: j.salesItem || null, account: j.account || null };
  });
  const quotationStages = f.quotationStages.map((st, i) => {
    if (!st.name.trim() || !st.role) problems.push(`Quotation stage ${i + 1} needs a name and a role`);
    const min = st.minAmount.trim() ? parseNumber(st.minAmount) : null;
    if (st.minAmount.trim() && (min === null || min < 0)) problems.push(`The amount of quotation stage ${i + 1} is not an amount`);
    return { name: st.name.trim(), role: st.role, minAmount: min };
  });
  const calStages = f.calStages.map((st, i) => {
    if (!st.name.trim() || !st.role) problems.push(`Job stage ${i + 1} needs a name and a role`);
    return { name: st.name.trim(), role: st.role };
  });
  const branch = f.branch.trim() ? Number(f.branch) : null;
  if (branch !== null && (!Number.isInteger(branch) || branch <= 0)) problems.push('The branch is an SAP branch number');
  if (problems.length) return { settings: null, problems: Array.from(new Set(problems)) };
  return {
    settings: {
      costTypes,
      profitPct: profit ?? 0,
      vatCode: f.vatCode || null,
      quoteValidityDays: validity,
      terms: f.terms.trim() || null,
      jobTypes,
      quotationStages,
      calStages,
      financeRole: f.financeRole || null,
      docMode: f.docMode,
      defaultSalesItem: f.defaultSalesItem || null,
      defaultRevenueAccount: f.defaultRevenueAccount || null,
      branchId: branch,
      dim5SalesItem: f.dim5SalesItem,
      postQuotations: f.postQuotations,
    },
    problems: [],
  };
}

const E2ISetup: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { companyId, companyName } = useCompanyId();
  const [setup, setSetup] = useState<E2ISetupData | null>(null);
  const [config, setConfig] = useState<E2IConfig | null>(null);
  const [form, setForm] = useState<SetupForm | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState<Section>('pricing');

  const load = useCallback(async () => {
    if (!companyId) { setError('Select a company first.'); return; }
    setError(null);
    try {
      const [s, c] = await Promise.all([e2iApi.setup(companyId), e2iApi.config(companyId).catch(() => null)]);
      setSetup(s);
      setConfig(c);
      setForm(formOf(s.settings));
      setDirty(false);
      setProblems([]);
    } catch (e: any) {
      setError(errorText(e, 'The Enquiry to Invoice setup could not be loaded'));
    }
  }, [companyId]);

  useEffect(() => { setSetup(null); setForm(null); void load(); }, [load]);

  const change = (patch: Partial<SetupForm>) => {
    setForm((f) => (f ? { ...f, ...patch } : f));
    setDirty(true);
  };

  const save = async () => {
    if (!form) return;
    const { settings, problems: found } = settingsOf(form);
    setProblems(found);
    if (!settings) return;
    setSaving(true);
    try {
      const saved = await e2iApi.saveSetup(companyId, settings);
      setSetup(saved);
      setForm(formOf(saved.settings));
      setDirty(false);
      dispatch(showSnackbar({ message: 'Enquiry to Invoice settings saved', severity: 'success' }));
    } catch (e: any) {
      dispatch(showSnackbar({ message: errorText(e, 'The settings could not be saved'), severity: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  if (error && !setup) {
    return (
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/e2i/enquiries')}>Enquiries</Button>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Box>
    );
  }
  if (!setup || !form) {
    return <Box sx={{ p: 3, display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={20} /><Typography>Loading…</Typography></Box>;
  }

  const roles = setup.roles || [];
  const vatCodes = config?.vatCodes || [];
  const currency = config?.currency || '';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 1300 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <IconButton aria-label="Back to the enquiries" onClick={() => navigate('/e2i/enquiries')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>Enquiry to Invoice setup</Typography>
        {companyName ? <Typography variant="body2" color="text.secondary">{companyName}</Typography> : null}
        <Box sx={{ flexGrow: 1 }} />
        {dirty ? <Typography variant="caption" color="text.secondary">Unsaved changes</Typography> : null}
        {dirty ? <Button variant="text" onClick={() => { setForm(formOf(setup.settings)); setDirty(false); setProblems([]); }} disabled={saving}>Discard</Button> : null}
        <Button onClick={() => { void save(); }} disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save settings'}</Button>
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {config ? (
          <Chip size="small" color={config.enabled ? 'success' : 'warning'} label={config.enabled ? 'Switched on for this company' : 'Switched off (Company Management)'} />
        ) : null}
        {config ? <Chip size="small" variant="outlined" label={config.vatRegistered ? 'VAT-registered' : 'Not VAT-registered'} /> : null}
      </Stack>
      {problems.length ? (
        <Alert severity="warning" onClose={() => setProblems([])}>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>{problems.map((p) => <li key={p}>{p}</li>)}</Box>
        </Alert>
      ) : null}

      <Tabs value={section} onChange={(_e, v) => setSection(v)} aria-label="Setup sections" variant="scrollable" allowScrollButtonsMobile sx={{ borderBottom: '1px solid #e3e8eb' }}>
        <Tab value="pricing" label="Pricing and quotations" />
        <Tab value="jobs" label={`Job types (${form.jobTypes.length})`} />
        <Tab value="approvals" label="Approvals" />
        <Tab value="sap" label="SAP documents" />
      </Tabs>

      {section === 'pricing' ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Cost types</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The estimate&apos;s lines are grouped by cost type. A quotation prices each cost line at unit cost × (1 + the cost type&apos;s markup) × (1 + the profit); the preparer can change the markups per quotation. A saved code cannot change, as the estimate lines keep it.
          </Typography>
          <CostTypesEditor rows={form.costTypes} onChange={(rows) => change({ costTypes: rows })} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField id="e2i-set-profit" label="Profit %" value={form.profit} onChange={(e) => change({ profit: e.target.value })} inputProps={{ inputMode: 'decimal' }} helperText="On top of the markups" fullWidth />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id="e2i-set-vat"
                select
                label="Default output VAT code"
                value={form.vatCode}
                onChange={(e) => change({ vatCode: e.target.value })}
                fullWidth
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                helperText={vatCodes.length ? 'Active SAP output VAT codes' : 'The SAP VAT codes could not be read'}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {form.vatCode && !vatCodes.some((v) => v.code === form.vatCode) ? <MenuItem value={form.vatCode}>{form.vatCode}</MenuItem> : null}
                {vatCodes.map((v) => <MenuItem key={v.code} value={v.code}>{v.code} · {v.name} ({formatPct(v.rate)})</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField id="e2i-set-validity" label="Quotation validity (days)" value={form.validity} onChange={(e) => change({ validity: e.target.value.replace(/[^\d]/g, '').slice(0, 4) })} inputProps={{ inputMode: 'numeric' }} fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField id="e2i-set-terms" label="Default quotation terms" value={form.terms} onChange={(e) => change({ terms: e.target.value.slice(0, 8000) })} multiline minRows={4} fullWidth helperText="Printed on the quotation; the preparer can change them per quotation" />
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {section === 'jobs' ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {form.docMode === 'Service'
              ? 'A job\'s type gives its lines their revenue account in SAP (service documents).'
              : 'A job\'s type gives its lines their SAP sales item (item documents); the item decides the revenue account.'}
          </Typography>
          <JobTypesEditor rows={form.jobTypes} onChange={(rows) => change({ jobTypes: rows })} companyId={companyId} docMode={form.docMode} />
        </Paper>
      ) : null}

      {section === 'approvals' ? (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Quotation approval</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Stages in order. Any user holding the stage&apos;s role (main or additional) who can open the company approves; never the quotation&apos;s preparer. A stage with an amount applies only to quotations of that total or more.
            </Typography>
            <StagesEditor idPrefix="e2i-set-qs" rows={form.quotationStages} onChange={(rows) => change({ quotationStages: rows })} roles={roles} withMinimum currency={currency} addLabel="Add quotation stage" />
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }}>Jobs without a quotation</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Stages a job without a quotation goes through before its sales order can be made. No stage: it is approved when created.</Typography>
            <StagesEditor idPrefix="e2i-set-cs" rows={form.calStages} onChange={(rows) => change({ calStages: rows })} roles={roles} addLabel="Add job stage" />
          </Paper>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Finance</Typography>
            <Box sx={{ maxWidth: 420 }}>
              <RoleSelect
                id="e2i-set-finance"
                label="Finance role (books invoice requests)"
                value={form.financeRole}
                roles={roles}
                onChange={(v) => change({ financeRole: v })}
                allowEmpty="Only users with the finance permission"
              />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Users holding this role, and users with the Enquiry to Invoice finance permission, book the invoice requests as SAP A/R invoice drafts and return them.
            </Typography>
          </Paper>
        </Stack>
      ) : null}

      {section === 'sap' ? (
        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" component="h2">Document lines</Typography>
              <RadioGroup aria-label="Document lines" value={form.docMode} onChange={(e) => change({ docMode: e.target.value as DocMode })}>
                <FormControlLabel value="Item" control={<Radio size="small" />} label="Item documents: each line is a sales item, quantity 1, priced at the line amount" />
                <FormControlLabel value="Service" control={<Radio size="small" />} label="Service documents: each line posts to a revenue account" />
              </RadioGroup>
            </Grid>
            <Grid item xs={12} md={6}>
              {form.docMode === 'Service' ? (
                <LineItemPicker
                  id="e2i-set-default-account"
                  companyId={companyId}
                  docMode="Service"
                  value={form.defaultRevenueAccount}
                  onChange={(code) => change({ defaultRevenueAccount: code })}
                  helperText="For lines whose job type names none"
                />
              ) : (
                <LineItemPicker
                  id="e2i-set-default-item"
                  companyId={companyId}
                  docMode="Item"
                  value={form.defaultSalesItem}
                  onChange={(code) => change({ defaultSalesItem: code })}
                  helperText="For lines whose job type names none"
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField id="e2i-set-branch" label="SAP branch" value={form.branch} onChange={(e) => change({ branch: e.target.value.replace(/[^\d]/g, '').slice(0, 9) })} inputProps={{ inputMode: 'numeric' }} helperText="Only for companies whose SAP uses branches" fullWidth />
            </Grid>
            {form.docMode === 'Item' ? (
              <Grid item xs={12}>
                <FormControlLabel control={<Switch checked={form.dim5SalesItem} onChange={(e) => change({ dim5SalesItem: e.target.checked })} />} label="Dimension 5 of each line = its sales item code" />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.postQuotations} onChange={(e) => change({ postQuotations: e.target.checked })} />} label="Post quotations to SAP (a sales quotation when one is sent to the client)" />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info">
                Sales orders and A/R invoices are made as SAP drafts: finance adds them in SAP through its approval procedure, and PRPO picks up the documents once they are added.
              </Alert>
            </Grid>
          </Grid>
        </Paper>
      ) : null}
    </Box>
  );
};

export default E2ISetup;
